import React, { Component } from 'react';
import { FixedSizeList as List } from 'react-window';
import { Alignment, SensitiveData } from '@philips-internal/oswald-ui-constants';
import { bool, func, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, isContentfulString, isFunction, isTouchableDevice } from '@philips-internal/oswald-ui-utils';
import { hideSensitiveData } from '../../../utils/hide-sensitive-data.js';
import { Autocomplete } from '../../autocomplete/autocomplete.js';
import { CollapseArrow } from '../../collapse-arrow/collapse-arrow.js';
import { CountryFlag } from '../../country-flag/country-flag.js';
import { Mapper } from '../../mapper/mapper.js';
import { Popover } from '../../popover/popover.js';
import phoneNumberData from '../phone-number-data.js';
import { CountryItem } from './country-item.js';
class CountrySelect extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            open: false,
        };
        this.onClose = () => {
            if (this.isDisabledOrReadOnly()) {
                return;
            }
            this.setState({ open: false });
        };
        this.onOpen = () => {
            if (this.isDisabledOrReadOnly()) {
                return;
            }
            this.setState({ open: true });
        };
    }
    findCountry(value) {
        if (!isContentfulString(value)) {
            return null;
        }
        return phoneNumberData.find(country => country.code.toUpperCase() === value.toUpperCase()) ?? null;
    }
    isDisabledOrReadOnly() {
        const { enabled, readOnly } = this.props;
        return Boolean(!enabled || readOnly);
    }
    getClassName() {
        const { enabled, readOnly } = this.props;
        return classNames({
            'c-country-select': true,
            'is-disabled': !enabled,
            'is-read-only': Boolean(readOnly),
        });
    }
    renderSelect() {
        const { readOnly, sensitiveData, value } = this.props;
        const { open } = this.state;
        const country = this.findCountry(value);
        let callingCode = country?.callingCode ? `+${country?.callingCode}` : '';
        let countryCode = country?.code ?? '';
        if (readOnly && sensitiveData === SensitiveData.PARTIALLY_HIDDEN) {
            callingCode = hideSensitiveData(callingCode, sensitiveData, { replacer: /[0-9]/g });
            countryCode = 'none';
        }
        if (readOnly && sensitiveData === SensitiveData.HIDDEN) {
            callingCode = '';
            countryCode = 'none';
        }
        return (React.createElement("div", { "aria-label": MessageService.translate('tws.phone-number.select-contry-aria-label'), className: this.getClassName(), onClick: this.onOpen, role: "button" },
            React.createElement(CountryFlag, { code: countryCode }),
            React.createElement("span", { className: "c-country-select__code" }, callingCode),
            isTouchableDevice() ? this.renderAutocomplete() : React.createElement(CollapseArrow, { collapsed: !open })));
    }
    renderMobile() {
        return (React.createElement("div", { className: "c-country-select__modal-wrapper" }, this.renderSelect()));
    }
    renderAutocomplete() {
        const { onChange } = this.props;
        const data = phoneNumberData
            .map(country => ({
            callingCode: country.callingCode,
            key: country.code,
            label: MessageService.translate(country.name),
        }))
            .sort((codeA, codeB) => codeA.label.localeCompare(codeB.label));
        const onAutocompleteChange = (event) => {
            if (isFunction(onChange) && event && event.value) {
                const selectedValue = event.value;
                const value = this.findCountry(String(selectedValue.key));
                onChange(new ChangeEvent({ value }));
            }
        };
        return (React.createElement(Autocomplete, { clearable: false, data: data, done: true, entry: "key", localData: true, onChange: onAutocompleteChange, primaryKey: "key", queryKey: "label", uuid: "fake-uuid" },
            React.createElement(Mapper, { map: { code: 'key' } },
                React.createElement(CountryFlag, { showName: true }))));
    }
    renderPopover() {
        const { onChange } = this.props;
        const { open } = this.state;
        const onPopoverItemClick = (event) => {
            this.onClose();
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({ value: event }));
            }
        };
        const data = phoneNumberData
            .map(phoneNumber => ({
            ...phoneNumber,
            name: MessageService.translate(phoneNumber.name),
        }))
            .sort((codeA, codeB) => codeA.name.localeCompare(codeB.name));
        return (React.createElement(Popover, { alignment: Alignment.LEFT, onClose: this.onClose, open: open, showArrow: false },
            React.createElement("div", { className: "c-country__selector" }, this.renderSelect()),
            React.createElement(Popover.Content, null,
                React.createElement(List, { height: 400, itemCount: phoneNumberData.length - 1, itemSize: 28, width: 380 }, ({ index, style }) => (React.createElement(CountryItem, { onClick: onPopoverItemClick, style: style, ...data[index] }))))));
    }
    render() {
        return isTouchableDevice()
            ? this.renderMobile()
            : this.renderPopover();
    }
}
CountrySelect.propTypes = {
    enabled: bool,
    onChange: func,
    readOnly: bool,
    sensitiveData: oneOf(enumValues(SensitiveData)),
    value: string,
};
CountrySelect.defaultProps = {
    enabled: true,
    sensitiveData: SensitiveData.NONE,
};
export { CountrySelect, };
//# sourceMappingURL=country-select.js.map